import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Landmark, Loader2 } from "lucide-react";

const PLAN_LABEL = { standard: "Standard · 69 € HT/mois", premium: "Premium · 129 € HT/mois" };

export default function SubscriptionReturn({ ok }) {
  const [params] = useSearchParams();
  const brId = params.get("br");
  const isMock = params.get("mock") === "1";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const tried = useRef(false);

  useEffect(() => {
    if (!ok || !brId || tried.current) return;
    tried.current = true;
    let stop = false;
    let count = 0;
    const tick = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/subscriptions/status/${brId}`);
        setInfo(data);
        if (["trialing", "active", "abandoned", "failed"].includes(data.status) || count > 8) return;
        count += 1;
        setTimeout(tick, 2000);
      } catch (e) {
        setError(formatErr(e?.response?.data?.detail) || e.message);
      }
    };
    tick();
    return () => { stop = true; };
  }, [ok, brId]);

  const done = info && ["trialing", "active"].includes(info.status);
  const failed = info && ["abandoned", "failed"].includes(info.status);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md w-full text-center" data-testid="subscription-return-card">
        {ok ? (
          <>
            {done ? <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto" />
              : failed ? <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              : <Loader2 className="h-16 w-16 text-[#618DB8] mx-auto animate-spin" />}
            <h1 className="font-display text-2xl font-extrabold mt-4 text-deep" data-testid="subscription-return-title">
              {done ? "Mandat SEPA signé" : failed ? "Mandat non finalisé" : "Vérification du mandat…"}
            </h1>
            <p className="text-slate-600 mt-2">
              {done && <>Votre abonnement <b>{PLAN_LABEL[info.plan] || info.plan}</b> est activé. Essai gratuit de 30 jours : aucun prélèvement avant le <b>{info.first_debit_date && new Date(info.first_debit_date).toLocaleDateString("fr-FR", { dateStyle: "long" })}</b>.</>}
              {failed && "Le mandat n'a pas été confirmé. Vous pouvez recommencer depuis la page Tarifs."}
              {!done && !failed && "Nous confirmons votre mandat de prélèvement auprès de GoCardless."}
            </p>
            {done && (
              <div className="mt-4 text-xs bg-slate-50 rounded-lg p-3 border border-slate-200 text-left space-y-1" data-testid="subscription-return-details">
                <div className="flex items-center gap-1.5 text-slate-500"><Landmark className="h-3.5 w-3.5" />Mandat : <span className="font-mono">{info.mandate_id}</span></div>
                <div className="text-slate-500">Prélèvement SEPA mensuel · résiliable à tout moment</div>
                {(isMock || info.mock) && <div className="text-amber-700 font-semibold" data-testid="subscription-mock-badge">Mode démonstration — clé GoCardless non configurée</div>}
              </div>
            )}
            {error && <div className="text-red-600 text-sm mt-3" data-testid="subscription-return-error">{error}</div>}
            <div className="mt-6 flex gap-2 justify-center">
              <Link to={failed ? "/tarifs" : "/dashboard"}>
                <Button className="btn-primary" data-testid="subscription-return-back">{failed ? "Retour aux tarifs" : "Accéder à mon espace"}</Button>
              </Link>
            </div>
          </>
        ) : (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto" />
            <h1 className="font-display text-2xl font-extrabold mt-4 text-deep">Souscription annulée</h1>
            <p className="text-slate-600 mt-2">Aucun mandat n'a été signé et rien ne sera prélevé. Vous pouvez réessayer à tout moment.</p>
            <Link to="/tarifs" className="block mt-6">
              <Button variant="outline" data-testid="subscription-cancel-back">Retour aux tarifs</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
